/**
 * Twin-stick touch input: left half = move, right half = shoot.
 *
 * The right stick is a "shoot stick" (Brawl-Stars style): drag to aim and
 * RELEASE to fire in that direction; a quick tap (released near center) fires
 * with auto-aim (aim ≈ 0, resolved to the nearest enemy by the sim). While
 * held, its live value drives the character's facing.
 */
import { vec2, length, type Vec2 } from "@allstars/shared";
import { createVirtualJoystick } from "./virtualJoystick.js";
import type { InputSample, InputSource } from "./types.js";

const TAP_THRESHOLD = 0.2; // below this on release = a tap → auto-aim

const makeZone = (side: "left" | "right"): HTMLElement => {
  const zone = document.createElement("div");
  zone.className = `vstick-zone vstick-zone--${side}`;
  return zone;
};

export const createTouchInput = (container: HTMLElement): InputSource => {
  const leftZone = makeZone("left");
  const rightZone = makeZone("right");
  container.appendChild(leftZone);
  container.appendChild(rightZone);

  const moveStick = createVirtualJoystick({ zone: leftZone });

  let pendingFire = false;
  let fireDir: Vec2 = vec2(0, 0);
  const aimStick = createVirtualJoystick({
    zone: rightZone,
    onRelease: (dir) => {
      pendingFire = true;
      fireDir = length(dir) >= TAP_THRESHOLD ? dir : vec2(0, 0);
    },
  });

  return {
    read: (): InputSample => {
      let aim = aimStick.value(); // live drag → facing while aiming
      let fire = false;
      if (pendingFire) {
        fire = true;
        aim = fireDir;
        pendingFire = false;
      }
      return { move: moveStick.value(), aim, fire };
    },
    get active(): boolean {
      // Stay "active" while a fire is pending so the merge reads our fire event.
      return moveStick.active || aimStick.active || pendingFire;
    },
    dispose(): void {
      moveStick.dispose();
      aimStick.dispose();
      leftZone.remove();
      rightZone.remove();
    },
  };
};
